/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { adminLogin, fetchAdminMe } from "@/lib/api";

const STORAGE_KEY = "mysitecare-admin-token";
const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => window.localStorage.getItem(STORAGE_KEY) || null);
  const [email, setEmail] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setChecking(false);
      return undefined;
    }
    fetchAdminMe(token)
      .then((me) => { if (!cancelled) setEmail(me.email); })
      .catch(() => {
        if (!cancelled) {
          setToken(null);
          setEmail(null);
          window.localStorage.removeItem(STORAGE_KEY);
        }
      })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token]);

  const login = useCallback(async (loginEmail, password) => {
    const res = await adminLogin(loginEmail, password);
    window.localStorage.setItem(STORAGE_KEY, res.token);
    setToken(res.token);
    setEmail(res.email);
    return res;
  }, []);

  const setSession = useCallback((sessionToken, sessionEmail) => {
    window.localStorage.setItem(STORAGE_KEY, sessionToken);
    setToken(sessionToken);
    setEmail(sessionEmail);
  }, []);

  const logout = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setEmail(null);
  }, []);

  const isLoggedIn = Boolean(token && email);
  const value = useMemo(() => ({
    token, email, checking, isLoggedIn, login, logout, setSession,
    revealAdminUi: isLoggedIn,
  }), [token, email, checking, isLoggedIn, login, logout, setSession]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
